import { useId } from 'react';
import type { JSX } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import type { NoteAsk } from './useStudioNoteMap.js';
import { Button } from '../ui/button';
import { Hint } from '../ui/icon-button';
import { Info } from 'lucide-react';

/**
 * P09b (Q-7) — the bar over the map beside a beat (mockup gm-studio-quests-preview.html mapPaneHtml()): «Map [select of
 * every map of the campaign] · this beat's map / not this beat's map», the hint of the moment («Click where the
 * players can find it…» while a place is chosen) and the question when a note is dropped on a map that is not its
 * beat's («“…” lives in the beat “…” — Make shared and place / Place, keep in the beat / Cancel»).
 */
export interface StudioMapBarProps {
  readonly pageId: string;
  readonly maps: readonly { readonly id: string; readonly name: string }[];
  readonly isBeatMap: boolean;
  readonly placing: boolean;
  readonly ask: NoteAsk | undefined;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onSelect: (pageId: string) => void;
  readonly onAnswer: (choice: 'share' | 'keep' | 'cancel') => void;
}

export function StudioMapBar({ pageId, maps, isBeatMap, placing, ask, t, onSelect, onAnswer }: StudioMapBarProps): JSX.Element {
  const selectId = useId();
  return (
    <div className="relative flex-none border-b border-line" data-testid="studio-mapbar">
      {/* One 32 px line (group T): the map of the pane, whose map it is, and the hint of the moment — the drop hint is
          an ⓘ, the «click where…» of a placing note is said in words (it is what the GM must do now). */}
      <div className="pane-section-head flex min-w-0 items-center gap-2 px-2 text-meta text-muted-foreground">
        <label htmlFor={selectId} className="flex-none">{t('studio.mapbar.map')}</label>
        <select
          id={selectId}
          className="h-6 min-w-0 max-w-[16rem] rounded border border-line bg-surface px-1.5 text-body-s text-fg focus:border-hl/50 focus:outline-none"
          value={pageId}
          onChange={(e): void => onSelect(e.target.value)}
        >
          {maps.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <span className="flex-none">{t(isBeatMap ? 'studio.mapbar.thisBeat' : 'studio.mapbar.otherBeat')}</span>
        {placing ? (
          <span className="min-w-0 truncate text-hl" title={t('studio.note.placeHint')} data-testid="studio-mapbar-hint">
            {t('studio.note.placeHint')}
          </span>
        ) : (
          <Hint text={t('studio.mapbar.dropHint')}>
            <span className="ml-auto flex flex-none text-dim" tabIndex={0} role="img" aria-label={t('studio.mapbar.dropHint')} data-testid="studio-mapbar-hint">
              <Info size={12} aria-hidden="true" />
            </span>
          </Hint>
        )}
      </div>
      {ask !== undefined ? (
        <div
          role="dialog"
          aria-label={ask.place}
          className="absolute left-2 top-full z-[60] mt-1 flex w-[320px] max-w-[calc(100%-1rem)] flex-col gap-2 rounded-md border border-line bg-panel-2 px-2.5 py-2 shadow-lg"
        >
          <strong className="text-ui text-fg">{ask.place}</strong>
          <span className="text-body-s text-muted-foreground">{t('studio.noteDrop.body')}</span>
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" size="sm" onClick={(): void => onAnswer('share')}>
              {t('studio.noteDrop.share')}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={(): void => onAnswer('keep')}>
              {t('studio.noteDrop.keep')}
            </Button>
            <button
              type="button"
              className="px-2 py-0.5 text-ui text-hl hover:underline"
              onClick={(): void => onAnswer('cancel')}
            >
              {t('studio.noteDrop.cancel')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
